import { t } from '@lingui/core/macro';
import { isPresent } from '@luminovo/commons';
import { SecondaryIconButton, useRouteMatches } from '@luminovo/design-system';
import { XIcon } from 'lucide-react';
import * as React from 'react';

export enum DialogLevel {
    Level1 = 'level1',
    Level2 = 'level2',
    Level3 = 'level3',
    Dynamic = 'dynamic',
}

const ModalContext = React.createContext<
    | {
          level1: {
              dialog: JSX.Element | null;
              setDialog: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          };
          level2: {
              dialog: JSX.Element | null;
              setDialog: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          };
          level3: {
              dialog: JSX.Element | null;
              setDialog: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          };
          drawer: JSX.Element | null;
          setDrawer: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          isIntercomLauncherHidden: boolean;
          requestHideIntercomLauncher: () => () => void;
      }
    | undefined
>(undefined);

// The level a dialog is rendered at, so content inside it can close it, whichever `useDialogContext` call opened it.
const EnclosingDialogLevelContext = React.createContext<DialogLevel | null>(null);

/**
 * Hook for managing modal dialogs with support for multiple layers/levels.
 *
 * The dialog system supports three levels of modals that can be stacked:
 * - level1: Base level dialog, used for primary/main modal content
 * - level2: Secondary level dialog, can be opened on top of level1 for nested workflows
 * - level3: Tertiary level dialog, highest level for critical confirmations or final steps
 *
 * Each level can only have one dialog open at a time. Opening a dialog at a specific
 * level when that level already has an open dialog will be prevented.
 *
 * The hook manages dialog state using refs to track the current and target dialog levels.
 * It automatically determines the appropriate level for new dialogs based on what's already
 * open, and provides methods to set, close individual dialogs, or close all dialogs at once.
 * `closeDialog` closes the dialog this hook call opened, or else the dialog it is rendered in.
 */
export function useDialogContext(defaultLevel: DialogLevel = DialogLevel.Level1) {
    const modalContext = React.useContext(ModalContext);
    if (!modalContext) {
        throw new Error("ModalContext is not present, make sure you've setup the ModalContext.Provider correctly.");
    }

    const { level1, level2, level3 } = modalContext;
    const enclosingLevel = React.useContext(EnclosingDialogLevelContext);
    const currentLevelRef = React.useRef<DialogLevel | null>(null);
    const targetLevel = React.useRef<DialogLevel | null>(null);

    React.useEffect(() => {
        if (isPresent(currentLevelRef.current)) {
            return;
        }

        if (defaultLevel !== DialogLevel.Dynamic) {
            targetLevel.current = defaultLevel;
            return;
        }

        if (!isPresent(level1.dialog)) {
            targetLevel.current = DialogLevel.Level1;
        } else if (!isPresent(level2.dialog)) {
            targetLevel.current = DialogLevel.Level2;
        } else if (!isPresent(level3.dialog)) {
            targetLevel.current = DialogLevel.Level3;
        }
    }, [defaultLevel, level1, level2, level3]);

    const setDialog = React.useCallback(
        (dialog: JSX.Element) => {
            if (targetLevel.current === DialogLevel.Level1) {
                currentLevelRef.current = DialogLevel.Level1;
                level1.setDialog(dialog);
            } else if (targetLevel.current === DialogLevel.Level2) {
                currentLevelRef.current = DialogLevel.Level2;
                level2.setDialog(dialog);
            } else if (targetLevel.current === DialogLevel.Level3) {
                currentLevelRef.current = DialogLevel.Level3;
                level3.setDialog(dialog);
            } else {
                level3.setDialog(null);
                level2.setDialog(null);
                level1.setDialog(dialog);
                currentLevelRef.current = DialogLevel.Level1;
            }
        },
        [level1, level2, level3],
    );

    const closeDialog = React.useCallback(() => {
        const level = currentLevelRef.current ?? enclosingLevel;
        if (level === DialogLevel.Level1) {
            level1.setDialog(null);
        } else if (level === DialogLevel.Level2) {
            level2.setDialog(null);
        } else if (level === DialogLevel.Level3) {
            level3.setDialog(null);
        }
        currentLevelRef.current = null;
        targetLevel.current = null;
    }, [level1, level2, level3, enclosingLevel]);

    const closeAllDialogs = React.useCallback(() => {
        level1.setDialog(null);
        level2.setDialog(null);
        level3.setDialog(null);
        currentLevelRef.current = null;
        targetLevel.current = null;
    }, [level1, level2, level3]);

    return {
        setDialog,
        closeDialog,
        closeAllDialogs,
        isOpen: isPresent(currentLevelRef.current),
    };
}

export function useDrawerContext() {
    const modalContext = React.useContext(ModalContext);
    if (!modalContext) {
        throw new Error("ModalContext is not present, make sure you've setup the ModalContext.Provider correctly.");
    }

    const { setDrawer, drawer } = modalContext;
    return { setDrawer, closeDrawer: () => setDrawer(null), isOpen: isPresent(drawer) };
}

/**
 * Hides the Intercom launcher (the floating chat bubble in the bottom-right corner) for as long
 * as the calling component is mounted. Use this for full-screen dialogs whose bottom-right controls
 * would otherwise be overlapped by the launcher (see MAN-2012).
 *
 * Requests are ref-counted so multiple consumers compose. The actual `window.Intercom` call lives in
 * `IntercomWidget`, which restores the correct per-user-type visibility once no consumer requests the
 * launcher to be hidden anymore.
 */
export function useHideIntercomLauncher() {
    const modalContext = React.useContext(ModalContext);
    if (!modalContext) {
        throw new Error("ModalContext is not present, make sure you've setup the ModalContext.Provider correctly.");
    }

    const { requestHideIntercomLauncher } = modalContext;
    React.useEffect(() => requestHideIntercomLauncher(), [requestHideIntercomLauncher]);
}

export function useIsIntercomLauncherHidden() {
    const modalContext = React.useContext(ModalContext);
    if (!modalContext) {
        throw new Error("ModalContext is not present, make sure you've setup the ModalContext.Provider correctly.");
    }

    return modalContext.isIntercomLauncherHidden;
}

export const CloseDrawerButton: React.FunctionComponent<{ disableMargin?: boolean; id?: string }> = ({
    disableMargin,
    id,
}): JSX.Element => {
    const { closeDrawer } = useDrawerContext();

    return (
        <SecondaryIconButton
            aria-label={t`Close`}
            size="small"
            onClick={() => closeDrawer()}
            style={{ margin: disableMargin ? undefined : '12px 24px 12px 16px' }}
            id={id}
        >
            <XIcon />
        </SecondaryIconButton>
    );
};

export function ModalContextProvider({ children }: React.PropsWithChildren<{}>) {
    const [dialogLevel1, setDialogLevel1] = React.useState<JSX.Element | null>(null);
    const [dialogLevel2, setDialogLevel2] = React.useState<JSX.Element | null>(null);
    const [dialogLevel3, setDialogLevel3] = React.useState<JSX.Element | null>(null);
    const [drawer, setDrawer] = React.useState<JSX.Element | null>(null);
    const [intercomLauncherHideCount, setIntercomLauncherHideCount] = React.useState(0);

    const requestHideIntercomLauncher = React.useCallback(() => {
        setIntercomLauncherHideCount((count) => count + 1);
        return () => setIntercomLauncherHideCount((count) => Math.max(0, count - 1));
    }, []);

    // The page on screen, which moves in the render that shows the next page: a dialog stays open while the next
    // page loads, and a drawer that page opens after it has rendered stays open (`MonitoringButton`).
    const pathname = useRouteMatches().at(-1)?.pathname;

    React.useEffect(() => {
        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setDialogLevel1(null);
        setDialogLevel2(null);
        setDialogLevel3(null);
        setDrawer(null);
    }, [pathname]);

    return (
        <ModalContext.Provider
            value={{
                level1: { dialog: dialogLevel1, setDialog: setDialogLevel1 },
                level2: { dialog: dialogLevel2, setDialog: setDialogLevel2 },
                level3: { dialog: dialogLevel3, setDialog: setDialogLevel3 },
                drawer,
                setDrawer,
                isIntercomLauncherHidden: intercomLauncherHideCount > 0,
                requestHideIntercomLauncher,
            }}
        >
            <>
                <EnclosingDialogLevelContext.Provider value={DialogLevel.Level3}>
                    {dialogLevel3}
                </EnclosingDialogLevelContext.Provider>
                <EnclosingDialogLevelContext.Provider value={DialogLevel.Level2}>
                    {dialogLevel2}
                </EnclosingDialogLevelContext.Provider>
                <EnclosingDialogLevelContext.Provider value={DialogLevel.Level1}>
                    {dialogLevel1}
                </EnclosingDialogLevelContext.Provider>
                {drawer}
                {children}
            </>
        </ModalContext.Provider>
    );
}
